import type { PriceDaily, RMR } from "epsm-api-types/models";
import { TIME_RANGE_MAP } from "../../../config";
import { type SectorsChartType, SectorsTimeRange } from "../../../types";
import { transformDate } from "../transformDate";

type PreparedStock = {
	Date: Date;
	Close: number;
	Symbol: string;
};

type ChartTypeHandler<T = PriceDaily | RMR> = (
	stocks: T[],
	timeFormat: SectorsTimeRange,
) => PreparedStock[];

type ChartData = PriceDaily[] | RMR[];

const chartTypeHandlers: Record<SectorsChartType, ChartTypeHandler> = {
	Price: (
		stocks: PriceDaily[],
		timeFormat: SectorsTimeRange,
	): PreparedStock[] => {
		const preparedClose = TIME_RANGE_MAP[timeFormat].closePrice;

		return stocks
			.filter((p) => p[preparedClose] !== undefined)
			.map((p) => ({
				Date:
					timeFormat === SectorsTimeRange.FifteenDays
						? new Date(p.date) // FifteenDays requires timezone conversion for proper display
						: transformDate(p.date), // Other ranges preserve original date/time without timezone conversion
				Close: p[preparedClose]!,
				Symbol: p.ticker,
			}));
	},

	Ratio: (stocks: RMR[]): PreparedStock[] => {
		return stocks
			.filter((p) => p.index_ratio !== undefined)
			.map((p) => ({
				Date: transformDate(p.date),
				Close: p.index_ratio! * 100, // Non-null assertion is safe because of the filter above
				Symbol: p.ticker,
			}));
	},
};

type Args = {
	chartType: SectorsChartType;
	stocks: PriceDaily[] | RMR[];
	timeFormat: SectorsTimeRange;
};

export function getPreparedStocks(args: Args): PreparedStock[] | undefined {
	const { chartType, stocks, timeFormat } = args;

	const handler = chartTypeHandlers[chartType];

	if (!handler) {
		console.warn(`Unsupported chart type: ${chartType}`);
		return undefined;
	}

	return handler(stocks as ChartData, timeFormat);
}
